import { Component, ViewChild, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDistance } from 'date-fns';
import { LoaderService } from 'src/core/services/loader.service';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { MedicineService } from 'src/core/services/medicine.service';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { Batch, BatchStatus } from 'src/interfaces/batch';
import { Medicine } from 'src/interfaces/medicine';
import { MedicineDosagePackageType } from 'src/interfaces/medicine-dosage-package-type';
import { PackageType } from 'src/interfaces/package-type';
import { format } from 'date-fns';

@Component({
    selector: 'core-batch-list',
    templateUrl: './batch-list.component.html',
    styleUrls: ['./batch-list.component.scss'],
    standalone: false
})
export class BatchListComponent implements OnInit {

  displayedColumns: string[] = [
    'id',
    'batch_number',
    'medicine',
    'quantity_produced',
    'status',
    'created_at',
    'updated_at',
  ];

  batchesDataSource: MatTableDataSource<Batch>;
  page: number = 1;
  limit: number = 15;
  totalNumberOfBatches: number = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  filterBatchesForm = this._formBuilder.group({
    batchNumber: [''],
    batchStatus: [null],
    medicineName: ['']
  });

  selectedMedicine: Medicine | null = null;
  selectedBatchStatus: BatchStatus | null = null;

  medicine: Medicine[] = [];
  batchStatuses: BatchStatus[] = [];

  isLoading: boolean = false;

  constructor(
    private _prescriptionService: PrescriptionService,
    private router: Router,
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    private _formBuilder: UntypedFormBuilder,
    private _loaderService: LoaderService,
    private _medicineService: MedicineService,
  ) {

  }

  ngOnInit(): void {

    const limit = this.localStorageService.get('BATCHES', 'LIMIT');

    if (limit) {
      this.limit = parseInt(limit);
    }

    this
      .route
      .data
      .subscribe(data => {
        this.batchesDataSource = new MatTableDataSource<Batch>(data.batches.batches);
        this.batchesDataSource.sort = this.sort;
        this.totalNumberOfBatches = this._prescriptionService.getTotalNumberOfBatches();
        this.page = data.batches.pageNumber;
        this.batchStatuses = data.batches.statuses;

        this.medicine = data.batches.medicine;

        // Initialize filters from query params (preserve on back from view)
        const qp = this.route.snapshot.queryParamMap;
        const qpBatchNumber = qp.get('batchNumber');
        const qpMedicineId = qp.get('medicineId');
        const qpBatchStatusId = qp.get('batchStatusId');

        if (qpBatchNumber) {
          this.filterBatchesForm.controls['batchNumber'].setValue(qpBatchNumber, { emitEvent: false });
        }

        if (qpBatchStatusId) {
          const statusId = parseInt(qpBatchStatusId);
          const status = this.batchStatuses.find(s => s.id === statusId) || null;
          this.selectedBatchStatus = status;
          this.filterBatchesForm.controls['batchStatus'].setValue(status, { emitEvent: false });
        }

        if (qpMedicineId) {
          const medId = parseInt(qpMedicineId);
          (async () => {
            const med = await this._medicineService.getMedicine(medId);
            if (med) {
              this.selectedMedicine = med;
              this.filterBatchesForm.controls['medicineName'].setValue(med.name, { emitEvent: false });
            }
            // Fetch list with initial filters (after medicine fetch attempt)
            this.page = 1;
            this.listBatches(
              this.page,
              this.limit,
              this.filterBatchesForm.value.batchNumber || '',
              this.selectedMedicine ? this.selectedMedicine.id : null,
              this.selectedBatchStatus ? this.selectedBatchStatus.id : null
            );
          })();
        } else {
          // Fetch list with initial filters if any (no medicine id case)
          if (qpBatchNumber || qpBatchStatusId) {
            this.page = 1;
            this.listBatches(
              this.page,
              this.limit,
              this.filterBatchesForm.value.batchNumber || '',
              this.selectedMedicine ? this.selectedMedicine.id : null,
              this.selectedBatchStatus ? this.selectedBatchStatus.id : null
            );
          }
        }
      });

    this
      ._loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this.filterBatchesForm
      .controls['batchNumber']
      .valueChanges
      .subscribe(value => {
        this.page = 1; // go to first page
        // get some batches...
        this.listBatches(
          this.page,
          this.limit,
          value,
          this.selectedMedicine ? this.selectedMedicine.id : null,
          this.selectedBatchStatus ? this.selectedBatchStatus.id : null
        );
      });

    this.filterBatchesForm
      .controls['medicineName']
      .valueChanges
      .subscribe(value => {
        // get some medicine...
        (async () => {
          this.medicine = await this._prescriptionService.getMedicine(value, 1, 15);
        })();
      });

    this.filterBatchesForm
      .controls['batchStatus']
      .valueChanges
      .subscribe(value => {
        this.page = 1; // go to first page
        // get some batches...

        this.selectedBatchStatus = value;
        this.listBatches(
          this.page,
          this.limit,
          this.filterBatchesForm.value.batchNumber,
          this.selectedMedicine ? this.selectedMedicine.id : null,
          value ? value.id : null
        );
      })

  }

  resetStatus() {
    this.filterBatchesForm
      .patchValue({
        batchStatus: null
      });
    this.selectedBatchStatus = null;
  }



  onPageChange(event: PageEvent) {
    // Get more batches 
    this.limit = event.pageSize;
    this.page = event.pageIndex + 1;

    this.localStorageService.store('BATCHES', 'LIMIT', this.limit.toString());
    this.localStorageService.store('BATCHES', 'PAGE', this.page.toString());

    this.listBatches(
      this.page,
      this.limit,
      this.filterBatchesForm.value.batchNumber,
      this.selectedMedicine ? this.selectedMedicine.id : null,
      this.selectedBatchStatus ? this.selectedBatchStatus.id : null
    );

  }

  listBatches(
    page: number = 1,
    limit: number = 10,
    search: string = "",
    medicine_id: number | null = null,
    batch_status_id: number | null = null
  ): void {

    (async () => {
      const newBatches = await this._prescriptionService.getBatches(
        page,
        limit,
        search,
        medicine_id,
        batch_status_id
      );

      const newData = [
        ...newBatches
      ]

      this.batchesDataSource.data = newData;

      this.totalNumberOfBatches = this._prescriptionService.getTotalNumberOfBatches();

    })();
  }


  medicineSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedMedicine = event.option.value;

    if (!this.selectedMedicine) return;

    this.filterBatchesForm.patchValue({
      medicineName: this.selectedMedicine.name,
    });

    this.page = 1; // go to first page

    // get some batches...
    this.listBatches(
      this.page,
      this.limit,
      this.filterBatchesForm.value.batchNumber,
      this.selectedMedicine ? this.selectedMedicine.id : null,
      this.selectedBatchStatus ? this.selectedBatchStatus.id : null
    );
  }

  resetMedicine() {

    this.filterBatchesForm
      .patchValue({
        medicineName: ""
      });

    this.selectedMedicine = null;

    this.page = 1; // go to first page

    // get some batches...
    this.listBatches(
      this.page,
      this.limit,
      this.filterBatchesForm.value.batchNumber,
      null,
      this.selectedBatchStatus ? this.selectedBatchStatus.id : null
    );
  }

 getCreationTime(batch: Batch) {
    return format(new Date(batch.created_at), 'MMMM dd, yyyy HH:mm');
}

  formatTime(time: string | null) {
    if (!time) return '-';
    return format(new Date(time), 'MMMM dd, yyyy HH:mm');
  }

  openBatch(id: number) {
    this.router.navigate(["portal/batches/view/", id], {
      queryParams: {
        batchNumber: this.filterBatchesForm.value.batchNumber || undefined,
        medicineId: this.selectedMedicine ? this.selectedMedicine.id : undefined,
        batchStatusId: this.selectedBatchStatus ? this.selectedBatchStatus.id : undefined
      }
    });
  }

  getQuantityProduced(batch: Batch): string {
    try {
      const packageType: PackageType = batch
        .medicine_dosage_type
        .medicine_dosage_package_type
        .filter(type =>  type.id === batch.medicine_dosage_package_type_id)[0]
        .package_type

      const medicineDosagePackageType: MedicineDosagePackageType = batch
        .medicine_dosage_type
        .medicine_dosage_package_type.filter(type =>  type.id === batch.medicine_dosage_package_type_id)[0];

      const quantityProduced: number = batch.quantity_produced;

      return packageType && medicineDosagePackageType ?
        `${quantityProduced} x ${packageType.type} (${medicineDosagePackageType.contained_quantity})`
        : '-';
    } catch (error) {
      return '-';
    }
  }

  getBadgeBackground(status: BatchStatus) {
    switch (status.code) {
      case 'AWM': // Awaiting review 
        return 'bg-dark';
      case 'AWP': // Awaiting fulfilment
        return 'bg-primary';
      case 'ISK': // Packaging 
        return 'bg-success';
      case 'DPL': // Shipped
        return 'bg-danger';

      default: return 'bg-secondary';

    }
  }

}
